import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { from, Observable, of } from 'rxjs'
import { catchError, concatMap, filter, map, mergeMap, switchMap, withLatestFrom, } from 'rxjs/operators'

import { ErrorReportingService } from '../../shared/services/error-reporting.service'
import { PayloadedAction } from '../../shared/types'
import { dispatchErrorAction } from '../../shared/utils'
import { AuthSecurityWrapperService } from '../auth/auth-security-wrapper/auth-security-wrapper.service'
import { authNameSearchRequest, } from '../auth/auth.actions'
import { selectAuthErrorCount, selectAuthMethod, selectIsSignedIn } from '../auth/auth.selectors'
import { AuthMethods, NameAuthRequestParams } from '../auth/auth.types'
import { CHARACTER_LIMIT_VEHICLE_COLOR } from '../location/location.constants'
import events from '../tagging/events'
import { TaggingService } from '../tagging/tagging.service'
import { handleAuthAnalytics } from '../tagging/tagging.utils'
import {
  cancelStepsAhead,
  openMessageDialog,
  openPromptDialog,
  setCaptchaModalOpen,
  setCaptchaVersion,
  setSplashscreenStep,
  showSplashscreen,
} from '../ui/ui.actions'
import { selectOrigin, selectQueryParamsVehicleData } from '../ui/ui.selectors'
import { MessageDialogTypes, Origin, PromptDialogTypes, } from '../ui/ui.types'
import { resetLoading } from '../ui/loading/loading.actions'
import { AAAStore } from '../../store/root-reducer'
import * as memberActions from './member.actions'
import { MemberService } from './member.service'
import {
  AddVehicleResponse,
  CaptchaVersion,
  MemberInfo,
  MEMBERSHIP_ERRORS,
  MemberStatusCodes,
  MemberVehicles,
  SearchMemberResult,
  Vehicle,
} from './member.types'
import { getAutomatedEventDetails, getEncodedAuthCredentials, isCaaZipCode, extractFuelTypes, needsEnrichment } from './member.utils'
import { RapService } from '../rap/rap.service'
import { selectUserSessionId } from '../session/session.selectors'
import { selectIsMotorcycleEligible, selectIsMotorhomeEligible, selectMemberActiveVehicle } from './member.selectors'
import { FuelType, GENERIC_MAKES, VehicleType } from '../vehicle/vehicle.types'
import { clearEditMetadata, ENRICH_VEHICLE_DETAILS, requestEnrichVehicleDetails, requestVehicleDetails } from '../vehicle/vehicle.actions'
import { CredentialsManagementService } from '../auth/credential-management/credential-management.service'
import auth from '../tagging/events/auth';
import { AdobeMemberValidationService } from '../tagging/adobe/member-validation-adobe.service'
import { AdobeEventTypes, FormSubmitStatus, MemberValidationType } from '../tagging/tagging.types'
import { AdobeEventService } from '../tagging/adobe/event-adobe.service'
import { ConfigService } from '../config/config.service'
import { setEligibilityVehicle } from './member.actions'
import { selectShouldOfferRenewalLink, selectMemberData, selectMemberVehicles } from './member.selectors'

@Injectable()
export class MemberEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AAAStore>,
    private _memberService: MemberService,
    private authSecurityWrapperService: AuthSecurityWrapperService,
    private taggingService: TaggingService,
    private adobeEventService: AdobeEventService,
    private adobeMemberValidationService: AdobeMemberValidationService,
    private errorReportingService: ErrorReportingService,
    private rapService: RapService,
    private credentialsService: CredentialsManagementService,
    private configService: ConfigService,
  ) { }
  getEncodedCredentials = (
    credentials: NameAuthRequestParams,
    userSessionId: string,
    errorCount: number) => getEncodedAuthCredentials(credentials, userSessionId, errorCount)

  loadMemberInfo$ = createEffect(
    (): Observable<
      | ReturnType<typeof memberActions.memberInfoSuccess>
      | ReturnType<typeof memberActions.memberInfoFailure>
    > =>
      this.actions$.pipe(
        ofType(memberActions.MEMBER_INFO.REQUEST, memberActions.MEMBER_INFO_BACKGROUND_POLL),
        withLatestFrom(this.store$.select(selectAuthMethod)),
        switchMap(([action, authMethod]) =>
          this._memberService.getInfo$().pipe(
            map((memberData: MemberInfo) =>
              memberActions.memberInfoSuccess({
                payload: memberData,
                background: action.type === memberActions.MEMBER_INFO_BACKGROUND_POLL,
              })
            ),
            catchError((error) => {
              const { reason } = getAutomatedEventDetails(error)

              handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
                reason,
                method: authMethod,
                isValid: false,
              })
              if (this.configService.getConfig().nativeAppView && error.response?.status === 403) {
                this.authSecurityWrapperService.forceLogout()
              }
              return this.errorReportingService.notifyErrorObservable(
                error,
                memberActions.memberInfoFailure
              )
            })
          )
        )
      )
  )


  handleIneligibility$ = createEffect(
    (): Observable<
      | ReturnType<typeof openMessageDialog>
      | ReturnType<typeof showSplashscreen>
      | ReturnType<typeof setSplashscreenStep>
    > =>
      this.actions$.pipe(
        ofType(
          memberActions.MEMBER_ELIGIBILITY_RESULT
        ),
        withLatestFrom(
          this.store$.select(selectAuthMethod),
          this.store$.select(selectMemberData),
          this.store$.select(selectShouldOfferRenewalLink),
        ),
        filter(
          ([action, _, __, isPendingGrace]: [PayloadedAction<MemberInfo>, AuthMethods, MemberInfo | null, boolean]) =>
            // Pending-grace members get the renewal bottom sheet instead of the
            // ineligibility dialog (openPendingGraceSheet$ handles them).
            (!action.payload.eligible || action.payload.ersAbuser) && !isPendingGrace
        ),
        concatMap(([action, authMethod, memberData]: [
          PayloadedAction<MemberInfo>,
          AuthMethods,
          MemberInfo | null,
          boolean,
        ]) => {
          const { eligible, ersAbuser } = action.payload
          const {
            hasRemainingEntitlements,
            codIndicator,
            statusCode,
            membershipRenewalRequired,
            fullMembershipNumber,
          } = memberData ?? {}

          if (!ersAbuser) {
            const showCoverageExhaustedMessage =
              hasRemainingEntitlements === false &&
              codIndicator === true &&
              (statusCode === MemberStatusCodes.ACTIVE || statusCode === MemberStatusCodes.PENDING)

            if (showCoverageExhaustedMessage && !membershipRenewalRequired) {
              handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
                reason: MEMBERSHIP_ERRORS.COVERAGE_EXHAUSTED.reason,
                method: authMethod,
                isValid: false,
                membershipNumber: fullMembershipNumber,
              })
              return [
                openMessageDialog({ payload: { type: MessageDialogTypes.COVERAGE_EXHAUSTED } }),
                showSplashscreen(),
                setSplashscreenStep({ payload: 0 }),
              ]
            }
          }

          const reason = eligible ? MEMBERSHIP_ERRORS.NOT_ELEGIBLE.reason :
            ersAbuser ? MEMBERSHIP_ERRORS.ERS_ABUSER.reason : 'Members without entitlements'

          handleAuthAnalytics(this.taggingService, this.adobeMemberValidationService, {
            reason,
            method: authMethod,
            isValid: false,
            membershipNumber: fullMembershipNumber,
          })
          return [
            openMessageDialog({
              payload: {
                type: MessageDialogTypes.INVALID_MEMBERSHIP,
              },
            }),
            showSplashscreen(),
            setSplashscreenStep({ payload: 0 }),
          ]
        })
      )
  )

  openPendingGraceSheet$ = createEffect(
    (): Observable<
      | ReturnType<typeof resetLoading>
      | ReturnType<typeof memberActions.setPendingGraceSheetOpen>
    > =>
      this.actions$.pipe(
        ofType(memberActions.MEMBER_ELIGIBILITY_RESULT),
        withLatestFrom(
          this.store$.select(selectShouldOfferRenewalLink),
          this.store$.select(selectOrigin),
        ),
        filter(
          ([_, isPendingGrace, origin]: [PayloadedAction<MemberInfo>, boolean, Origin | null]) =>
            // A quick-renew return shows the payment-in-process sheet instead
            // (member-quick-renew.effects.ts / the MEMBER_INFO.SUCCESS reducer
            // case) — the renewal sheet would otherwise win the race, since
            // this effect's chain resolves after that synchronous reducer update.
            isPendingGrace && origin !== Origin.QUICK_RENEW
        ),
        switchMap(() => {
          this.adobeEventService.sendEvent({
            eventName: AdobeEventTypes.SYSTEM,
            eventValue: auth.PENDING_GRACE_SHEET_SHOWN,
          })
          // The call-status flow short-circuits to memberEligibilityResult for
          // pending-grace members, so CALL_STATUS.SUCCESS never fires and its
          // loading flag stays set — leaving the sign-in Confirm button greyed
          // out behind the renewal sheet. Clear it so the member can continue.
          return [
            resetLoading(),
            memberActions.setPendingGraceSheetOpen({ payload: true }),
          ]
        })
      )
  )

  loadMemberVehicles$ = createEffect(
    (): Observable<
      | ReturnType<typeof memberActions.vehicleLoadSuccess>
      | ReturnType<typeof memberActions.notifyVehicleLoadFailure>
    > =>
      this.actions$.pipe(
        ofType(memberActions.VEHICLE_LOAD.REQUEST),
        withLatestFrom(
          this.store$.pipe(select(selectQueryParamsVehicleData)),
        ),
        switchMap(([_, queryParamsVehicle]: [PayloadedAction, Vehicle]) =>
          this._memberService.getVehicles$().pipe(
            withLatestFrom(
              this.store$.select(selectIsMotorcycleEligible),
              this.store$.select(selectIsMotorhomeEligible)
            ),
            mergeMap(([vehiclesData, isMotorcycleEligible, isMotorhomeEligible]: [MemberVehicles, boolean, boolean]) => {
              const actions: any[] = []
              if (vehiclesData?.vehicles?.length > 0) {
                vehiclesData.vehicles = vehiclesData.vehicles.filter(({ make }: Vehicle) =>(
                  make.toLowerCase() !== GENERIC_MAKES.MOTORHOME_RV || isMotorhomeEligible
                ) && (
                  make.toLowerCase() !== GENERIC_MAKES.MOTORCYCLE || isMotorcycleEligible
                ))
                actions.push(memberActions.vehicleLoadSuccess({ payload: vehiclesData }))
              } else if (queryParamsVehicle) {
                let vehicle: Vehicle = { ...queryParamsVehicle }
                const payload = { vehicles: [vehicle] }
                actions.push(memberActions.vehicleLoadSuccess({ payload }))
                actions.push(memberActions.assignExistingVehicle({ payload: vehicle }))
              }

              // If there are no vehicles and there is no query params, complete the load with an empty array
              if(!vehiclesData?.vehicles?.length && !queryParamsVehicle) {
                actions.push(memberActions.vehicleLoadSuccess({ payload: { vehicles: [] } }))
              }

              return actions
            }),
            catchError((error) =>
              this.errorReportingService.notifyErrorObservable(
                error,
                memberActions.notifyVehicleLoadFailure
              )
            )
          )
        )
      )
  )

  setEligibilityVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReturnType<typeof setEligibilityVehicle>>(memberActions.SET_ELIGIBILITY_VEHICLE),
      withLatestFrom(this.store$.select(selectIsSignedIn)),
      // Details endpoint requires auth; post-login flows re-request them
      filter(([_, isSignedIn]) => isSignedIn),
      map(([action]) => requestVehicleDetails({
        payload: {
          year: Number(action.payload.year),
          make: action.payload.make,
          model: action.payload.model
        }
      }))
    )
  )

  handleVehicleEnrichmentSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ENRICH_VEHICLE_DETAILS.SUCCESS),
      map((action: PayloadedAction) => {
        const details = action.payload.enrichedDetails.details || [];
        const fuelTypes = extractFuelTypes(details);
        const fuelType = fuelTypes.length === 1 ? fuelTypes[0] : FuelType.U_K;
        const resolvedFuelType = action.payload.vehicle?.fuelType || fuelType;
        const resolvedVehicleType = action.payload.vehicle?.vehicleType || details[0]?.vehicleType;

        this.adobeEventService.sendEvent({
          eventName: AdobeEventTypes.SYSTEM,
          eventValue: events.vehicle.VEHICLE_ENRICHMENT_SUCCESS
        },
        null,
        {
          event_detail: `Fuel Type: ${fuelType} / Vehicle Type: ${details[0]?.vehicleType || 'Not found'}`
        }
        );

        return memberActions.setEnrichedVehicle({
          payload: {
            vehicle: {
              ...(action.payload.vehicle),
              fuelType: resolvedFuelType,
              vehicleType: resolvedVehicleType,
              isEnriched: true,
            } as Vehicle,
            addMemberVehicle: action.payload.addMemberVehicle,
          }
        });
      })
    )
  )

  handleVehicleEnrichmentFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ENRICH_VEHICLE_DETAILS.FAILURE),
      map((action: PayloadedAction) => {
        const { vehicle, addMemberVehicle } = action.payload

        this.adobeEventService.sendEvent({
          eventName: AdobeEventTypes.SYSTEM,
          eventValue: events.vehicle.VEHICLE_ENRICHMENT_FAILURE,
        })

        return memberActions.setEnrichedVehicle({
          payload: {
            vehicle: {
              ...vehicle,
              fuelType: vehicle.fuelType || FuelType.U_K,
              isEnriched: true,
            } as Vehicle,
            addMemberVehicle,
            skipVehicleUpdate: true,
          }
        })
      })
    )
  )

  storeUpdatedVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        memberActions.SET_VEHICLE,
        memberActions.SET_ENRICHED_VEHICLE
      ),
      withLatestFrom(
        this.store$.pipe(select(selectMemberActiveVehicle)),
        this.store$.pipe(select(selectMemberVehicles))
      ),
      switchMap(([action, activeVehicle, memberVehicles]: [PayloadedAction, Vehicle, Vehicle[]]) => {
        const vehicle = action.payload.vehicle || action.payload;
        vehicle.color = vehicle.color?.substr(
          0,
          CHARACTER_LIMIT_VEHICLE_COLOR
        )

        const actions = []
        if (needsEnrichment(vehicle)) {
          actions.push(requestEnrichVehicleDetails({ payload: { vehicle, addMemberVehicle: true } }))
        } else if (vehicle.id) {
          // skipVehicleUpdate (e.g. enrichment failure fallback) merges into app
          // state via the SET_ENRICHED_VEHICLE reducer only — no PUT.
          if (!action.payload.skipVehicleUpdate) {
            const isUpdateActiveVehicle = activeVehicle?.id === vehicle.id
            if (isUpdateActiveVehicle) {
              actions.push(memberActions.setVehicleStateOnly({ payload: vehicle }))
            }
            actions.push(memberActions.requestVehicleUpdate({ payload: vehicle }))
          }
        } else if (action.payload.addMemberVehicle || action.payload.addMemberVehicle === undefined) {
          actions.push(memberActions.requestVehicleAdd({ payload: vehicle }))
        } else {
          const vehiclesDetailUpdated = memberVehicles.map(memberVehicle => {
            if (vehicle.year === memberVehicle.year
                && vehicle.make === memberVehicle.make
                && vehicle.model === memberVehicle.model) {
              return {
                ...vehicle,
                ...memberVehicle,
              }
            }
            return memberVehicle;
          });
          actions.push(memberActions.vehicleLoadSuccess({ payload: { vehicles: vehiclesDetailUpdated } }))
        }
        return actions
      })
    )
  )

  addVehicle$ = createEffect(
    (): Observable<
      | ReturnType<typeof memberActions.vehicleAddSuccess>
      | ReturnType<typeof memberActions.notifyVehicleAddFailure>
    > =>
      this.actions$.pipe(
        ofType(memberActions.VEHICLE_ADD.REQUEST),
        mergeMap(
          (action: ReturnType<typeof memberActions.requestVehicleAdd>) => this._memberService.addVehicle$(action.payload).pipe(
            map((response: AddVehicleResponse) =>
              memberActions.vehicleAddSuccess({
                payload: {
                  ...action.payload,
                  id: response.id,
                },
              })
            ),
            catchError((error) =>
              this.errorReportingService.notifyErrorObservable(
                error,
                memberActions.notifyVehicleAddFailure
              )
            )
          )
        )
      )
  )

  updateMemberVehicle$ = createEffect(
    (): Observable<
      | ReturnType<typeof memberActions.vehicleUpdateSuccess>
      | ReturnType<typeof memberActions.notifyVehicleUpdateFailure>
    > =>
      this.actions$.pipe(
        ofType(memberActions.VEHICLE_UPDATE.REQUEST),
        mergeMap((action: PayloadedAction) => {
          this.taggingService.setAutomatedEvent(
            events.vehicle.VEHICLE_EDIT,
            events.vehicle.VEHICLE_PAGE_TYPE
          )

          const updateVehicle$ = !this.rapService.isRapUser()
            ? this._memberService.updateVehicle$(action.payload)
            : of(null)

          return updateVehicle$.pipe(
            map(() =>
              memberActions.vehicleUpdateSuccess({ payload: action.payload })
            ),
            catchError((error) =>
              this.errorReportingService.notifyErrorObservable(
                error,
                memberActions.notifyVehicleUpdateFailure
              )
            )
          )
        })
      )
  )

  deleteMemberVehicle$ = createEffect(
    (): Observable<
      | ReturnType<typeof memberActions.vehicleDeleteSuccess>
      | ReturnType<typeof memberActions.notifyVehicleDeleteFailure>
    > =>
      this.actions$.pipe(
        ofType(memberActions.VEHICLE_DELETE.REQUEST),
        switchMap((action: PayloadedAction) => {
          this.adobeEventService.sendEvent({
            eventName: AdobeEventTypes.CTA,
            eventValue: events.vehicle.VEHICLE_DELETE,
          })
          this.taggingService.setAutomatedEvent(
            events.vehicle.VEHICLE_DELETE,
            events.vehicle.VEHICLE_PAGE_TYPE
          )

          const deleteVehicle$ = !this.rapService.isRapUser()
            ? this._memberService.deleteVehicle$(action.payload)
            : of(null)

          return deleteVehicle$.pipe(
            switchMap(() =>
              [
                memberActions.vehicleDeleteSuccess({ payload: action.payload }),
                clearEditMetadata(),
              ]
            ),
            catchError((error) =>
              this.errorReportingService.notifyErrorObservable(
                error,
                memberActions.notifyVehicleDeleteFailure
              )
            )
          )
        })
      )
  )

  deleteMemberVehicleSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.VEHICLE_DELETE.SUCCESS),
      withLatestFrom(
        this.store$.pipe(select(selectMemberActiveVehicle))
      ),
      filter(([action, vehicle]: [PayloadedAction<string>, Vehicle]) => vehicle?.id === action.payload),
      switchMap(() => [memberActions.clearActiveVehicle(), cancelStepsAhead()])
    )
  )

  handleMemberSearch$ = createEffect(
    (): Observable<
      | ReturnType<typeof memberActions.searchMembersSuccess>
      | ReturnType<typeof memberActions.searchMembersFailure>
    > =>
      this.actions$.pipe(
        ofType(memberActions.MEMBERS_SEARCH.REQUEST),
        withLatestFrom(
          this.store$.select(selectUserSessionId),
          this.store$.select(selectAuthErrorCount),
        ),
        switchMap(([action, userSessionId, errorCount]: [PayloadedAction, string, number]) =>
          this._memberService.memberSearch$(action.payload).pipe(
            map((response: SearchMemberResult) => {
              if (action.payload.rememberMe) {
                this.credentialsService.storeCredentials({
                  firstName: action.payload.firstName,
                  lastName: action.payload.lastName,
                  zipCode: action.payload.zipCode,
                })
              }
              if (isCaaZipCode(action.payload.zipCode)) {
                this.taggingService.setAutomatedEvent(auth.CAA_ZIP_FORMAT_AUTH_NAME, auth.PAGE_TYPE)
              }
              window.localStorage.setItem('remember-credentials', action.payload.rememberMe)
              return memberActions.searchMembersSuccess({ payload: response })
            }),
            catchError((error) => {
              const { reason } = getAutomatedEventDetails(error)
              const encodedCredentials = this.getEncodedCredentials(action.payload, userSessionId, errorCount)

              this.adobeMemberValidationService.sendEvent(
                AdobeEventTypes.MEMBER_VALIDATION_RETURN,
                MemberValidationType.MEMBER_NAME,
                {
                  form_submit_status: FormSubmitStatus.FAIL,
                  ...(reason ? { error_type: reason } : {}),
                }
              )

              this.taggingService.setAutomatedEvent(
                events.auth.FORM_NAME_AUTH_FAILURE,
                events.auth.PAGE_TYPE, null,
                {
                  propertyName: encodedCredentials,
                  ...(reason ? { 'Event Detail': reason } : {}),
                  ...(error.response?.status ? { status: error.response.status } : {})
                }
              )

              const actions = []
              if (MEMBERSHIP_ERRORS.CAPTCHA_TOKEN.reason === reason) {
                actions.push(
                  setCaptchaModalOpen({ payload: true }),
                  setCaptchaVersion({ payload: CaptchaVersion.V2 })
                )
              } else {
                actions.push(dispatchErrorAction(memberActions.searchMembersFailure, error))
              }

              return this.errorReportingService.notifyErrorObservable(error, actions)
            })
          )
        )
      )
  )

  handleMemberSearchSuccess$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(memberActions.MEMBERS_SEARCH.SUCCESS),
      map(
        (action: ReturnType<typeof memberActions.searchMembersSuccess>) => {
          const { members, searchId } = action.payload
          const resultId = members[0]['resultId']

          return members.length > 1
            ? openPromptDialog({
              payload: {
                type: PromptDialogTypes.ADDITIONAL_AUTH_INFO,
                params: {
                  members,
                  searchId,
                },
              },
            })
            : authNameSearchRequest({ payload: { searchId, resultId } })
        }
      )
    )
  )

  autoEnrichVehicleDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.VEHICLE_LOAD.SUCCESS),
      switchMap((action: PayloadedAction<MemberVehicles | Vehicle>) => {
        let vehicles: Vehicle[] = 'vehicles' in action.payload && Array.isArray(action.payload.vehicles)
          ? action.payload.vehicles
          : [action.payload as Vehicle]

        return from(vehicles
          .filter((vehicle) => needsEnrichment(vehicle))
          .map((vehicle) => requestEnrichVehicleDetails({ payload: { vehicle, addMemberVehicle: false } })));
      })
    )
  )

  // The active vehicle set before sign-in (e.g. native roadside config) is not
  // part of the VEHICLE_LOAD.SUCCESS payload, so autoEnrichVehicleDetails$
  // never picks it up; enrich it here once the member vehicles load post-login
  autoEnrichActiveVehicle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(memberActions.VEHICLE_LOAD.SUCCESS),
      withLatestFrom(
        this.store$.select(selectMemberActiveVehicle),
        this.store$.select(selectIsSignedIn),
      ),
      filter(([action, activeVehicle, isSignedIn]: [PayloadedAction<MemberVehicles>, Vehicle, boolean]) =>
        isSignedIn &&
        !!activeVehicle &&
        needsEnrichment(activeVehicle) &&
        // avoid a duplicate request when autoEnrichVehicleDetails$ already covers it
        !(action.payload?.vehicles || []).some((vehicle) =>
          needsEnrichment(vehicle) &&
          vehicle.year == activeVehicle.year &&
          vehicle.make === activeVehicle.make &&
          vehicle.model === activeVehicle.model
        )
      ),
      map(([_, activeVehicle]) =>
        requestEnrichVehicleDetails({ payload: { vehicle: activeVehicle, addMemberVehicle: false } })
      )
    )
  )

}
